import React from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Button } from '../components/ui/button';
import { isRTL } from '../i18n';

const languages = [
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
];

export const LanguageSwitcher = ({ variant = 'default' }) => {
  const { i18n } = useTranslation();

  const currentLang = languages.find(lang => lang.code === i18n.language) || languages[0];

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    document.documentElement.dir = isRTL(langCode) ? 'rtl' : 'ltr';
    document.documentElement.lang = langCode;
  };

  if (variant === 'compact') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="text-white/50 hover:text-white hover:bg-white/5 px-2"
            data-testid="language-switcher"
          >
            <span className="text-base mr-1">{currentLang.flag}</span>
            <span className="text-xs uppercase">{currentLang.code}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="bg-black border-white/10 rounded-xl min-w-[140px]"
        >
          {languages.map((lang) => (
            <DropdownMenuItem
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              className={`flex items-center gap-2 cursor-pointer rounded-lg ${
                lang.code === i18n.language
                  ? 'bg-white/10 text-white'
                  : 'text-white/70 hover:text-white hover:bg-white/5'
              }`}
              data-testid={`lang-${lang.code}`}
            >
              <span className="text-base">{lang.flag}</span>
              <span className="text-sm">{lang.name}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Default inline variant for header
  return (
    <div className="flex items-center gap-1" data-testid="language-switcher-inline">
      <Globe className="w-4 h-4 text-white/40 mr-1" />
      {languages.map((lang, index) => (
        <React.Fragment key={lang.code}>
          <button
            onClick={() => changeLanguage(lang.code)}
            className={`text-xs font-medium transition-colors px-1 ${
              lang.code === i18n.language
                ? 'text-white'
                : 'text-white/40 hover:text-white/70'
            }`}
            data-testid={`lang-inline-${lang.code}`}
          >
            {lang.code.toUpperCase()}
          </button>
          {index < languages.length - 1 && (
            <span className="text-white/20">|</span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

export default LanguageSwitcher;
